import React, { useState } from 'react'
import { useQuery, useStore } from '@livestore/react'
import { Project, events } from '@work-squared/shared/schema'
import { getProjects$ } from '@work-squared/shared/queries'

type Event = ReturnType<(typeof events)[keyof typeof events]>

interface ProjectPickerProps {
  contactId: string
  existingProjectIds: string[]
  onClose: () => void
}

export const ProjectPicker: React.FC<ProjectPickerProps> = ({
  contactId,
  existingProjectIds,
  onClose,
}) => {
  const projects = useQuery(getProjects$) ?? []
  const { store } = useStore()
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(
    new Set(existingProjectIds)
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleToggleProject = (projectId: string) => {
    const newSelected = new Set(selectedProjectIds)
    if (newSelected.has(projectId)) {
      newSelected.delete(projectId)
    } else {
      newSelected.add(projectId)
    }
    setSelectedProjectIds(newSelected)
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const eventsToCommit: Event[] = []

      // Find projects to add (selected but not in existing)
      const projectsToAdd = Array.from(selectedProjectIds).filter(
        id => !existingProjectIds.includes(id)
      )
      // Find projects to remove (in existing but not selected)
      const projectsToRemove = existingProjectIds.filter(id => !selectedProjectIds.has(id))

      // Add events for new associations
      projectsToAdd.forEach(projectId => {
        eventsToCommit.push(
          events.projectContactAdded({
            id: crypto.randomUUID(),
            projectId,
            contactId,
            createdAt: new Date(),
          })
        )
      })

      // Add events for removed associations
      projectsToRemove.forEach(projectId => {
        eventsToCommit.push(
          events.projectContactRemoved({
            projectId,
            contactId,
          })
        )
      })

      if (eventsToCommit.length > 0) {
        await store.commit(...eventsToCommit)
      }
      onClose()
    } catch (error) {
      console.error('Failed to update project associations:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className='fixed inset-0 backdrop-blur-sm flex items-start justify-center pt-5 px-4 z-[9999]'
      onClick={onClose}
    >
      <div
        className='bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto'
        onClick={e => e.stopPropagation()}
      >
        <div>
          <h3 className='text-lg font-medium leading-6 text-gray-900'>
            Manage Project Associations
          </h3>

          {projects.length === 0 ? (
            <p className='mt-4 text-sm text-gray-500'>
              No projects available. Create some projects first.
            </p>
          ) : (
            <>
              <div className='mt-4 space-y-2 max-h-60 overflow-y-auto'>
                {projects.map((project: Project) => (
                  <label
                    key={project.id}
                    className='flex items-start p-3 rounded-lg hover:bg-gray-50 cursor-pointer'
                  >
                    <input
                      type='checkbox'
                      checked={selectedProjectIds.has(project.id)}
                      onChange={() => handleToggleProject(project.id)}
                      className='mt-1 mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
                    />
                    <div className='flex-1'>
                      <p className='text-sm font-medium text-gray-900'>{project.name}</p>
                      {project.description && (
                        <p className='text-xs text-gray-500 mt-1'>{project.description}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              <div className='mt-5 sm:mt-6 flex justify-end space-x-3'>
                <button
                  type='button'
                  onClick={onClose}
                  className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50'
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className='px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  {isSubmitting ? 'Updating...' : 'Update Associations'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
